import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X, BedDouble } from "lucide-react";

/**
 * Bulk room assignment for the Program Bookings table. GEHM (and above)
 * multi-selects participants, picks an accommodation + a specific room,
 * and the modal upserts a `room_allocations` row per booking via the
 * existing `POST /api/room-allocations` endpoint — no new backend needed.
 */
export default function BulkRoomAssignModal({ open, onClose, selectedIds = [], rows = [], onDone }) {
  const [accs, setAccs] = useState([]);
  const [allocs, setAllocs] = useState([]);        // to compute existing occupancy
  const [accId, setAccId] = useState("");
  const [roomName, setRoomName] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOver, setConfirmOver] = useState(false);   // second-tap over-capacity override

  useEffect(() => {
    if (!open) return;
    api.get("/accommodations").then(({ data }) => setAccs(data || [])).catch(() => setAccs([]));
    api.get("/room-allocations").then(({ data }) => setAllocs(data || [])).catch(() => setAllocs([]));
    setAccId(""); setRoomName("");
  }, [open]);

  const selectedRows = rows.filter((r) => selectedIds.includes(r.id));
  const acc = accs.find((a) => a.id === accId) || null;
  const roomOptions = (acc?.rooms || []).filter(r => r.name || r.room_name);
  const roomMeta = roomOptions.find(r => (r.name || r.room_name) === roomName) || null;
  // Beds already occupied in this room by participants NOT in our selection.
  const occupied = allocs.filter(a => a.accommodation_id === accId && a.room === roomName
                                       && !selectedIds.includes(a.booking_id)).length;
  const capacity = Number(roomMeta?.beds || roomMeta?.capacity || 0);
  const wouldFill = occupied + selectedRows.length;
  const overCapacity = capacity > 0 && wouldFill > capacity;

  const apply = async () => {
    if (!acc) { toast.error("Pick an accommodation"); return; }
    if (!roomName.trim()) { toast.error("Pick a room"); return; }
    // Over-capacity guard: first tap warns, second tap overrides.
    if (overCapacity && !confirmOver) {
      setConfirmOver(true);
      toast.error(`Room ${roomName} has ${capacity} bed${capacity === 1 ? "" : "s"} — ${occupied} already taken. Adding ${selectedRows.length} would exceed capacity. Tap Assign again to override.`);
      return;
    }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const b of selectedRows) {
      try {
        await api.post("/room-allocations", {
          booking_id: b.id, room: roomName.trim(),
          accommodation_id: acc.id,
          participant_name: b.customer || "",
          bed_no: "",
        });
        ok++;
      } catch { fail++; }
    }
    setBusy(false);
    setConfirmOver(false);
    if (ok) toast.success(`Assigned ${ok} participant${ok === 1 ? "" : "s"} to ${roomName} · ${acc.name}`);
    if (fail) toast.error(`${fail} assignment${fail === 1 ? "" : "s"} failed`);
    if (ok) onDone?.();
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         data-testid="bulk-room-assign-modal" onClick={() => !busy && onClose()}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[color:var(--aii-border)] flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-brand font-bold">Bulk assign</div>
            <h3 className="font-display text-lg font-semibold flex items-center gap-1.5"><BedDouble size={18}/> Room / accommodation</h3>
            <p className="text-xs text-[color:var(--aii-muted)] mt-0.5">Assigning to {selectedRows.length} participant{selectedRows.length === 1 ? "" : "s"}.</p>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 text-[color:var(--aii-muted)] hover:text-red-600"><X size={16}/></button>
        </div>
        <div className="p-4 space-y-3 text-sm">
          <label className="block">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Accommodation</div>
            <select value={accId} onChange={(e) => { setAccId(e.target.value); setRoomName(""); }}
                    className="input-aii w-full" data-testid="bulk-room-acc-select">
              <option value="">— pick a property —</option>
              {accs.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.city}, {a.country}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-[color:var(--aii-muted)] mb-1">Room</div>
            <select value={roomName} onChange={(e) => { setRoomName(e.target.value); setConfirmOver(false); }}
                    disabled={!acc || roomOptions.length === 0}
                    className="input-aii w-full disabled:opacity-60" data-testid="bulk-room-room-select">
              <option value="">{acc ? (roomOptions.length ? "— pick a room —" : "This property has no rooms yet") : "— pick a property first —"}</option>
              {roomOptions.map((r) => {
                const n = r.name || r.room_name;
                const beds = Number(r.beds || r.capacity || 0);
                const taken = allocs.filter(a => a.accommodation_id === accId && a.room === n).length;
                return <option key={n} value={n}>{n}{beds ? ` · ${taken}/${beds} beds taken` : ""}</option>;
              })}
            </select>
          </label>
          {roomName && capacity > 0 && (
            <div className={`text-xs rounded px-3 py-2 border ${overCapacity ? "bg-red-50 border-red-200 text-red-800" : wouldFill === capacity ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`}
                 data-testid="bulk-room-capacity-hint">
              {roomName} · {occupied} taken · {selectedRows.length} being added ·{" "}
              <b>{wouldFill}/{capacity} beds{overCapacity ? " (over capacity)" : ""}</b>
              {overCapacity && !confirmOver && " — tap Assign to see override option"}
            </div>
          )}
          <ul className="text-xs text-[color:var(--aii-muted)] max-h-32 overflow-auto border border-[color:var(--aii-border)] rounded p-2 bg-slate-50">
            {selectedRows.slice(0, 12).map((r) => <li key={r.id}>· {r.customer} <span className="text-[10px]">({r.destination}/{r.city})</span></li>)}
            {selectedRows.length > 12 && <li>+ {selectedRows.length - 12} more…</li>}
          </ul>
        </div>
        <div className="p-3 border-t border-[color:var(--aii-border)] flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="px-3 py-2 text-sm border border-[color:var(--aii-border)] rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={apply} disabled={busy || !accId || !roomName} data-testid="bulk-room-apply"
                  className={`px-4 py-2 text-sm text-white font-semibold rounded-md disabled:opacity-50 ${overCapacity && confirmOver ? "bg-red-600 hover:bg-red-700" : "bg-brand hover:bg-brand-dark"}`}>
            {busy ? "Assigning…" : overCapacity && confirmOver ? `Override & assign ${selectedRows.length}` : `Assign to ${selectedRows.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}
